/**
 * Usage Card Component
 * ====================
 * Displays usage metrics with progress bar for seat/client limits.
 */

import { Link } from 'react-router-dom'

interface UsageCardProps {
  label: string
  current: number
  max: number
  icon: React.ReactNode
  upgradeLink?: string
  upgradeText?: string
}

export function UsageCard({
  label,
  current,
  max,
  icon,
  upgradeLink = '/dashboard/subscription',
  upgradeText = 'Upgrade',
}: UsageCardProps) {
  const isUnlimited = max === -1
  const percentage = isUnlimited ? 0 : max === 0 ? 100 : Math.min(100, Math.round((current / max) * 100))
  const isAtLimit = !isUnlimited && current >= max
  const isNearLimit = !isUnlimited && percentage >= 80

  const getStatusColor = () => {
    if (isUnlimited) return 'green'
    if (isAtLimit) return 'red'
    if (isNearLimit) return 'yellow'
    return 'blue'
  }

  const statusColor = getStatusColor()

  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      icon: 'text-blue-600',
      bar: 'bg-blue-600',
      barBg: 'bg-blue-100',
    },
    green: {
      bg: 'bg-green-50',
      icon: 'text-green-600',
      bar: 'bg-green-600',
      barBg: 'bg-green-100',
    },
    yellow: {
      bg: 'bg-yellow-50',
      icon: 'text-yellow-600',
      bar: 'bg-yellow-500',
      barBg: 'bg-yellow-100',
    },
    red: {
      bg: 'bg-red-50',
      icon: 'text-red-600',
      bar: 'bg-red-600',
      barBg: 'bg-red-100',
    },
  }

  const colors = colorClasses[statusColor]

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colors.bg} ${colors.icon}`}>
            {icon}
          </div>
          <div>
            <p className="text-sm text-gray-600">{label}</p>
            <p className="text-xl font-bold text-gray-900">
              {current.toLocaleString()}
              <span className="text-gray-400 font-normal">
                {' / '}
                {isUnlimited ? 'Unlimited' : max.toLocaleString()}
              </span>
            </p>
          </div>
        </div>
        {isAtLimit && (
          <Link
            to={upgradeLink}
            className="text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            {upgradeText}
          </Link>
        )}
      </div>

      {/* Progress bar */}
      {!isUnlimited && (
        <div className="space-y-1">
          <div className={`h-2 rounded-full ${colors.barBg}`}>
            <div
              className={`h-2 rounded-full transition-all ${colors.bar}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">{percentage}% used</span>
            {isAtLimit ? (
              <span className="text-red-600 font-medium">Limit reached</span>
            ) : isNearLimit ? (
              <span className="text-yellow-600">
                {max - current} remaining
              </span>
            ) : (
              <span className="text-gray-500">
                {max - current} available
              </span>
            )}
          </div>
        </div>
      )}

      {isUnlimited && (
        <p className="text-xs text-green-600">No limit on your plan</p>
      )}
    </div>
  )
}

interface UsageSummaryProps {
  clientCurrent: number
  clientMax: number
  seatCurrent?: number
  seatMax?: number
  showSeats?: boolean
}

export function UsageSummary({
  clientCurrent,
  clientMax,
  seatCurrent = 0,
  seatMax = 0,
  showSeats = false,
}: UsageSummaryProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <UsageCard
        label="Clients"
        current={clientCurrent}
        max={clientMax}
        icon={
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        }
      />
      {showSeats && (
        <UsageCard
          label="Team Seats"
          current={seatCurrent}
          max={seatMax}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          }
        />
      )}
    </div>
  )
}
